import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { clientAPI, authAPI } from '../services/api';

const ClientSettings = () => {
    const { user, setUser, currentWorkspace } = useAuth();
    const { isInternalAdmin } = usePermissions();
    const [client, setClient] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    // Auto-dismiss the success toast so it doesn't linger.
    useEffect(() => {
        if (!success) return undefined;
        const t = setTimeout(() => setSuccess(null), 4000);
        return () => clearTimeout(t);
    }, [success]);
    const [formData, setFormData] = useState({
        name: '',
        timezone: 'UTC',
        diarization_provider: 'in_house',
        transcript_provider: 'deepgram',
        default_telephony_provider: 'twilio'
    });
    const [metabaseDashboards, setMetabaseDashboards] = useState([]);
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState('');
    const fileInputRef = useRef(null);

    // Show "Set password" for users who haven't set one yet (OAuth users)
    // has_password may be undefined for old cached sessions — treat undefined + provider present as OAuth
    const isOAuthUser = useMemo(() => {
        if (user?.has_password === true) return false;
        if (user?.has_password === false) return true;
        // Fallback for cached sessions without has_password field
        return user?.provider != null && user.provider !== '';
    }, [user]);

    // Account / profile state
    const [profileSuccess, setProfileSuccess] = useState(null);
    const [profileError, setProfileError] = useState(null);
    const [passwordSuccess, setPasswordSuccess] = useState(null);
    const [passwordError, setPasswordError] = useState(null);
    const [profileData, setProfileData] = useState({
        name: user?.name || '',
        email: user?.email || ''
    });

    // Sync profileData when user populates after mount (e.g. /me/access fills
    // in name/email asynchronously after a fresh-incognito refresh).
    useEffect(() => {
        if (user) {
            setProfileData({
                name: user.name || '',
                email: user.email || ''
            });
        }
    }, [user?.id, user?.name, user?.email]);
    const [passwordData, setPasswordData] = useState({
        current_password: '',
        password: '',
        password_confirmation: ''
    });

    useEffect(() => {
        const fetchClient = async () => {
            try {
                const clientId = currentWorkspace?.id || user?.client_id;
                if (!clientId) {
                    setError('No client associated with this user');
                    setLoading(false);
                    return;
                }

                const response = await clientAPI.getClient(clientId);
                const clientData = response.data?.data || response.data;

                if (!clientData) {
                    setError('Invalid response format from server');
                    return;
                }

                setClient(clientData);
                setFormData({
                    name: clientData.name || '',
                    timezone: clientData.timezone || 'UTC',
                    diarization_provider: clientData.diarization_provider || 'in_house',
                    transcript_provider: clientData.transcript_provider || 'deepgram',
                    default_telephony_provider: clientData.default_telephony_provider || 'twilio'
                });
                setMetabaseDashboards(clientData.metabase_dashboards || []);
                if (clientData.logo_url) {
                    setLogoPreview(clientData.logo_url);
                }
            } catch (err) {
                console.error('Error fetching client:', err);
                setError(err.response?.data?.error || err.message || 'Failed to fetch client data');
            } finally {
                setLoading(false);
            }
        };

        fetchClient();
    }, [user, currentWorkspace]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('client[name]', formData.name);
            formDataToSend.append('client[timezone]', formData.timezone);
            formDataToSend.append('client[diarization_provider]', formData.diarization_provider);
            formDataToSend.append('client[transcript_provider]', formData.transcript_provider);
            formDataToSend.append('client[default_telephony_provider]', formData.default_telephony_provider);
            // Only internal admins may change this list — the API enforces it, and
            // sending it as a non-admin is simply ignored. An EMPTY list still has
            // to be sent as an explicit blank, because FormData cannot encode an
            // empty array and a missing key now means "leave it alone" (it used to
            // mean "wipe it", which silently detached dashboards on every rename).
            if (isInternalAdmin) {
                if (metabaseDashboards.length === 0) {
                    formDataToSend.append('client[metabase_dashboards]', '');
                } else {
                    metabaseDashboards.forEach((dashboard, index) => {
                        formDataToSend.append(`client[metabase_dashboards][${index}][name]`, dashboard.name);
                        formDataToSend.append(`client[metabase_dashboards][${index}][metabase_id]`, dashboard.metabase_id);
                    });
                }
            }
            if (logoFile) {
                formDataToSend.append('client[logo]', logoFile);
            }

            const clientId = currentWorkspace?.id || user?.client_id;
            const response = await clientAPI.updateClient(clientId, formDataToSend);
            const updatedClient = response.data?.data || response.data;
            setClient(updatedClient);
            setSuccess('Workspace settings updated successfully');
        } catch (err) {
            console.error('Error updating client:', err);
            setError(err.response?.data?.error || err.message || 'Failed to update workspace settings');
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setLogoFile(file);
            const reader = new FileReader();
            reader.onloadend = () => { setLogoPreview(reader.result); };
            reader.readAsDataURL(file);
        }
    };

    // Account handlers
    const handleProfileChange = (e) => {
        const { name, value } = e.target;
        setProfileData(prev => ({ ...prev, [name]: value }));
    };

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswordData(prev => ({ ...prev, [name]: value }));
    };

    const handleProfileSubmit = async (e) => {
        e.preventDefault();
        setProfileSuccess(null);
        setProfileError(null);
        try {
            const response = await authAPI.updateProfile(profileData);
            if (response?.data?.user) {
                setUser((prev) => ({ ...prev, ...response.data.user }));
                setProfileSuccess('Profile updated successfully');
            }
        } catch (err) {
            setProfileError(err.response?.data?.error || 'Failed to update profile');
        }
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        setPasswordSuccess(null);
        setPasswordError(null);
        if (passwordData.password !== passwordData.password_confirmation) {
            setPasswordError('Passwords do not match');
            return;
        }
        try {
            await authAPI.updatePassword(passwordData);
            setPasswordSuccess('Password updated successfully');
            setPasswordData({ current_password: '', password: '', password_confirmation: '' });
        } catch (err) {
            setPasswordError(err.response?.data?.error || 'Failed to update password');
        }
    };

    const handleSetPassword = async (e) => {
        e.preventDefault();
        setPasswordSuccess(null);
        setPasswordError(null);
        if (passwordData.password !== passwordData.password_confirmation) {
            setPasswordError('Passwords do not match');
            return;
        }
        try {
            const res = await authAPI.setPassword(passwordData.password, passwordData.password_confirmation);
            setPasswordSuccess(res?.status?.message || 'Password set successfully!');
            setPasswordData({ current_password: '', password: '', password_confirmation: '' });
        } catch (err) {
            setPasswordError(err.response?.data?.error || 'Failed to set password');
        }
    };

    if (loading) {
        return <div className="text-sm">Loading...</div>;
    }

    return (
        <div className="space-y-8">
            {/* ── Workspace Settings ── */}
            <div className="bg-card rounded-lg border border-border">
                <div className="p-4">
                    {success && (
                        <div className="fixed top-4 right-4 z-[100] rounded-md bg-green-50 border border-green-200 shadow-lg p-4">
                            <div className="flex">
                                <svg className="h-5 w-5 text-green-400 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                <p className="ml-3 text-sm font-medium text-green-800">{success}</p>
                            </div>
                        </div>
                    )}
                    {error && <div className="mb-4 text-sm text-red-600">{error}</div>}

                    <form onSubmit={handleSubmit}>
                        <div className="space-y-8">
                            {/* Workspace ID */}
                            <div className="grid grid-cols-3 gap-8">
                                <div className="col-span-1">
                                    <h3 className="text-sm font-medium text-foreground">Workspace ID / Client ID</h3>
                                    <p className="mt-1 text-sm text-muted-foreground">Read-only identifier — useful for support requests and API calls.</p>
                                </div>
                                <div className="col-span-2">
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="text"
                                            value={client?.id ?? ''}
                                            readOnly
                                            className="block w-full rounded-md border-input bg-muted/50 text-muted-foreground font-mono text-sm shadow-sm focus:outline-none"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (client?.id != null) {
                                                    navigator.clipboard.writeText(String(client.id));
                                                    setSuccess('Workspace ID copied to clipboard');
                                                }
                                            }}
                                            className="inline-flex items-center px-3 py-2 border border-input shadow-sm text-sm font-medium rounded-md text-foreground bg-card hover:bg-muted/50"
                                        >
                                            Copy
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Name */}
                            <div className="grid grid-cols-3 gap-8">
                                <div className="col-span-1">
                                    <h3 className="text-sm font-medium text-foreground">Workspace name</h3>
                                    <p className="mt-1 text-sm text-muted-foreground">Your workspace name will be visible to all members.</p>
                                </div>
                                <div className="col-span-2">
                                    <input type="text" name="name" value={formData.name} onChange={handleChange} className="block w-full rounded-md border-input shadow-sm focus:border-black focus:ring-black text-sm" required />
                                </div>
                            </div>

                            {/* Logo */}
                            <div className="grid grid-cols-3 gap-8">
                                <div className="col-span-1">
                                    <h3 className="text-sm font-medium text-foreground">Company logo</h3>
                                    <p className="mt-1 text-sm text-muted-foreground">This will be displayed in your workspace and communications.</p>
                                </div>
                                <div className="col-span-2">
                                    <div className="flex items-center space-x-5">
                                        <div className="flex-shrink-0">
                                            {logoPreview ? (
                                                <img src={logoPreview} alt="Logo preview" className="h-16 w-16 object-cover rounded-lg" />
                                            ) : (
                                                <div className="h-16 w-16 rounded-lg bg-muted flex items-center justify-center">
                                                    <svg className="h-8 w-8 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                    </svg>
                                                </div>
                                            )}
                                        </div>
                                        <div>
                                            <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/*" className="hidden" />
                                            <button type="button" onClick={() => fileInputRef.current?.click()} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black">
                                                Change logo
                                            </button>
                                            <p className="mt-1 text-sm text-muted-foreground">PNG, JPG, GIF up to 10MB</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Timezone */}
                            <div className="grid grid-cols-3 gap-8">
                                <div className="col-span-1">
                                    <h3 className="text-sm font-medium text-foreground">Time zone</h3>
                                    <p className="mt-1 text-sm text-muted-foreground">The workspace time zone will affect time-dependent features across the application.</p>
                                </div>
                                <div className="col-span-2">
                                    <select name="timezone" value={formData.timezone} onChange={handleChange} className="block w-full rounded-md border-input shadow-sm focus:border-black focus:ring-black text-sm">
                                        <option value="UTC">UTC</option>
                                        <option value="America/New_York">Eastern Time</option>
                                        <option value="America/Chicago">Central Time</option>
                                        <option value="America/Denver">Mountain Time</option>
                                        <option value="America/Los_Angeles">Pacific Time</option>
                                        <option value="Asia/Kolkata">Asia/Kolkata</option>
                                    </select>
                                </div>
                            </div>

                            {/* Diarization + Transcript providers — internal admins only */}
                            {isInternalAdmin && (
                            <>
                            <div className="grid grid-cols-3 gap-8">
                                <div className="col-span-1">
                                    <h3 className="text-sm font-medium text-foreground">Speaker Diarization</h3>
                                    <p className="mt-1 text-sm text-muted-foreground">Choose how speaker identification is performed during audio analysis.</p>
                                </div>
                                <div className="col-span-2 space-y-3">
                                    <label className="flex items-start cursor-pointer">
                                        <input type="radio" name="diarization_provider" value="in_house" checked={formData.diarization_provider === 'in_house'} onChange={handleChange} className="mt-0.5 h-4 w-4 text-black border-input focus:ring-black" />
                                        <div className="ml-3">
                                            <span className="block text-sm font-medium text-foreground">In-House (pyannote + Silero)</span>
                                            <span className="block text-sm text-muted-foreground">Default. Uses on-premise speaker diarization with pyannote and Silero VAD.</span>
                                        </div>
                                    </label>
                                    <label className="flex items-start cursor-pointer">
                                        <input type="radio" name="diarization_provider" value="assembly_ai" checked={formData.diarization_provider === 'assembly_ai'} onChange={handleChange} className="mt-0.5 h-4 w-4 text-black border-input focus:ring-black" />
                                        <div className="ml-3">
                                            <span className="block text-sm font-medium text-foreground">AssemblyAI Diarization</span>
                                            <span className="block text-sm text-muted-foreground">Uses AssemblyAI speaker labels from the transcript for speaker identification.</span>
                                        </div>
                                    </label>
                                </div>
                            </div>

                            {/* Transcript Provider */}
                            <div className="grid grid-cols-3 gap-8">
                                <div className="col-span-1">
                                    <h3 className="text-sm font-medium text-foreground">Default Telephony Carrier</h3>
                                    <p className="mt-1 text-sm text-muted-foreground">The carrier new agents in this workspace use by default. Each carrier dials from its own default number (set in Admin → Phone Numbers).</p>
                                </div>
                                <div className="col-span-2">
                                    <div className="inline-flex rounded-lg border border-input overflow-hidden">
                                        {['twilio', 'plivo', 'vobiz'].map((c) => (
                                            <label
                                                key={c}
                                                className={`px-5 py-2 text-sm font-medium capitalize cursor-pointer transition-colors ${c !== 'twilio' ? 'border-l border-input' : ''} ${formData.default_telephony_provider === c ? 'bg-black text-white' : 'bg-white text-foreground hover:bg-muted'}`}
                                            >
                                                <input type="radio" name="default_telephony_provider" value={c} checked={formData.default_telephony_provider === c} onChange={handleChange} className="sr-only" />
                                                {c}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-3 gap-8">
                                <div className="col-span-1">
                                    <h3 className="text-sm font-medium text-foreground">Transcript Provider</h3>
                                    <p className="mt-1 text-sm text-muted-foreground">Choose the default provider for generating transcripts from call recordings.</p>
                                </div>
                                <div className="col-span-2 space-y-3">
                                    <label className="flex items-start cursor-pointer">
                                        <input type="radio" name="transcript_provider" value="deepgram" checked={formData.transcript_provider === 'deepgram'} onChange={handleChange} className="mt-0.5 h-4 w-4 text-black border-input focus:ring-black" />
                                        <div className="ml-3">
                                            <span className="block text-sm font-medium text-foreground">Deepgram</span>
                                            <span className="block text-sm text-muted-foreground">Default. Uses Deepgram Nova-2 for accurate transcription with speaker diarization and timestamps.</span>
                                        </div>
                                    </label>
                                    <label className="flex items-start cursor-pointer">
                                        <input type="radio" name="transcript_provider" value="assembly_ai" checked={formData.transcript_provider === 'assembly_ai'} onChange={handleChange} className="mt-0.5 h-4 w-4 text-black border-input focus:ring-black" />
                                        <div className="ml-3">
                                            <span className="block text-sm font-medium text-foreground">AssemblyAI</span>
                                            <span className="block text-sm text-muted-foreground">Uses AssemblyAI for transcription with speaker labels and utterance detection.</span>
                                        </div>
                                    </label>
                                </div>
                            </div>
                            </>
                            )}

                            {/* Metabase Dashboards — internal admins only */}
                            {isInternalAdmin && (
                            <div className="grid grid-cols-3 gap-8">
                                <div className="col-span-1">
                                    <h3 className="text-sm font-medium text-foreground">Metabase Dashboards</h3>
                                    <p className="mt-1 text-sm text-muted-foreground">Add Metabase dashboard IDs to embed them in the Analytics page.</p>
                                </div>
                                <div className="col-span-2 space-y-3">
                                    {metabaseDashboards.map((dashboard, index) => (
                                        <div key={index} className="flex items-center gap-3">
                                            <input
                                                type="text"
                                                placeholder="Dashboard name"
                                                value={dashboard.name}
                                                onChange={(e) => {
                                                    const updated = [...metabaseDashboards];
                                                    updated[index] = { ...updated[index], name: e.target.value };
                                                    setMetabaseDashboards(updated);
                                                }}
                                                className="flex-1 rounded-md border-input shadow-sm focus:border-black focus:ring-black text-sm"
                                            />
                                            <input
                                                type="text"
                                                placeholder="Metabase ID"
                                                value={dashboard.metabase_id}
                                                onChange={(e) => {
                                                    const updated = [...metabaseDashboards];
                                                    updated[index] = { ...updated[index], metabase_id: e.target.value };
                                                    setMetabaseDashboards(updated);
                                                }}
                                                className="w-36 rounded-md border-input shadow-sm focus:border-black focus:ring-black text-sm"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setMetabaseDashboards(metabaseDashboards.filter((_, i) => i !== index))}
                                                className="text-red-500 hover:text-red-700 text-sm font-medium"
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    ))}
                                    <button
                                        type="button"
                                        onClick={() => setMetabaseDashboards([...metabaseDashboards, { name: '', metabase_id: '' }])}
                                        className="inline-flex items-center px-3 py-1.5 border border-input shadow-sm text-sm font-medium rounded-md text-foreground bg-card hover:bg-muted/50"
                                    >
                                        + Add Dashboard
                                    </button>
                                </div>
                            </div>
                            )}

                            <div className="flex justify-end pt-4">
                                <button type="submit" className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black">
                                    Save Changes
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>

            {/* ── Account / Profile ── */}
            <div className="bg-card rounded-lg border border-border">
                <div className="px-4 py-3 border-b border-border">
                    <h3 className="text-sm font-semibold text-foreground">Account</h3>
                    <p className="mt-0.5 text-sm text-muted-foreground">Your personal profile and login credentials.</p>
                </div>
                <div className="p-4">
                    {profileSuccess && (
                        <div className="mb-4 rounded-md bg-green-50 p-4">
                            <div className="flex">
                                <svg className="h-5 w-5 text-green-400 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                <p className="ml-3 text-sm font-medium text-green-800">{profileSuccess}</p>
                            </div>
                        </div>
                    )}
                    {profileError && <div className="mb-4 text-sm text-red-600">{profileError}</div>}

                    <form onSubmit={handleProfileSubmit}>
                        <div className="space-y-8">
                            <div className="grid grid-cols-3 gap-8">
                                <div className="col-span-1">
                                    <h3 className="text-sm font-medium text-foreground">Name</h3>
                                    <p className="mt-1 text-sm text-muted-foreground">Your display name visible to other workspace members.</p>
                                </div>
                                <div className="col-span-2">
                                    <input type="text" name="name" value={profileData.name} onChange={handleProfileChange} className="block w-full rounded-md border-input shadow-sm focus:border-black focus:ring-black text-sm" required />
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-8">
                                <div className="col-span-1">
                                    <h3 className="text-sm font-medium text-foreground">Email</h3>
                                    <p className="mt-1 text-sm text-muted-foreground">Your email address used for login and notifications.</p>
                                </div>
                                <div className="col-span-2">
                                    <input type="email" value={profileData.email} disabled className="block w-full rounded-md border-border bg-muted/50 text-muted-foreground text-sm cursor-not-allowed" />
                                </div>
                            </div>
                            <div className="flex justify-end pt-4">
                                <button type="submit" className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black">
                                    Update profile
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>

            {/* ── Password ── */}
            <div className="bg-card rounded-lg border border-border">
                <div className="px-4 py-3 border-b border-border">
                    <h3 className="text-sm font-semibold text-foreground">
                        {isOAuthUser ? 'Set password' : 'Change password'}
                    </h3>
                    <p className="mt-0.5 text-sm text-muted-foreground">
                        {isOAuthUser
                            ? 'You signed in with Google. Set a password to also enable email + password login.'
                            : 'Update your login password.'}
                    </p>
                </div>
                <div className="p-4">
                    {passwordSuccess && (
                        <div className="mb-4 rounded-md bg-green-50 p-4">
                            <div className="flex">
                                <svg className="h-5 w-5 text-green-400 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                <p className="ml-3 text-sm font-medium text-green-800">{passwordSuccess}</p>
                            </div>
                        </div>
                    )}
                    {passwordError && <div className="mb-4 text-sm text-red-600">{passwordError}</div>}

                    <form onSubmit={isOAuthUser ? handleSetPassword : handlePasswordSubmit}>
                        <div className="space-y-8">
                            {!isOAuthUser && (
                                <div className="grid grid-cols-3 gap-8">
                                    <div className="col-span-1">
                                        <h3 className="text-sm font-medium text-foreground">Current password</h3>
                                        <p className="mt-1 text-sm text-muted-foreground">Enter your existing password to verify your identity.</p>
                                    </div>
                                    <div className="col-span-2">
                                        <input type="password" name="current_password" value={passwordData.current_password} onChange={handlePasswordChange} className="block w-full rounded-md border-input shadow-sm focus:border-black focus:ring-black text-sm" required />
                                    </div>
                                </div>
                            )}
                            <div className="grid grid-cols-3 gap-8">
                                <div className="col-span-1">
                                    <h3 className="text-sm font-medium text-foreground">{isOAuthUser ? 'Password' : 'New password'}</h3>
                                    <p className="mt-1 text-sm text-muted-foreground">Must be at least 8 characters long.</p>
                                </div>
                                <div className="col-span-2">
                                    <input type="password" name="password" value={passwordData.password} onChange={handlePasswordChange} className="block w-full rounded-md border-input shadow-sm focus:border-black focus:ring-black text-sm" required minLength={8} />
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-8">
                                <div className="col-span-1">
                                    <h3 className="text-sm font-medium text-foreground">Confirm password</h3>
                                    <p className="mt-1 text-sm text-muted-foreground">Re-enter your password to confirm.</p>
                                </div>
                                <div className="col-span-2">
                                    <input type="password" name="password_confirmation" value={passwordData.password_confirmation} onChange={handlePasswordChange} className="block w-full rounded-md border-input shadow-sm focus:border-black focus:ring-black text-sm" required minLength={8} />
                                </div>
                            </div>
                            <div className="flex justify-end pt-4">
                                <button type="submit" className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black">
                                    {isOAuthUser ? 'Set password' : 'Change password'}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ClientSettings;
